import React, { useCallback, useState, useRef, useEffect } from 'react';

import { fg } from '@confluence/feature-gating';
import { useIntl, defineMessages } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Text } from '@atlaskit/primitives/compiled/text';
import Pressable from '@atlaskit/primitives/pressable';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import Lozenge from '@atlaskit/lozenge/lozenge';

import { manuallyLogExpExposure } from '@confluence/feature-experiments';
import { usePageSpaceKey } from '@confluence/page-context';
import { isPersonalSpace } from '@confluence/space-utils/entry-points/isPersonalSpace.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { useFetch3PAuthentications } from './hooks/use3PAuthentication.ts';
import { ReviewContentSuggestionsModal } from './ReviewContentSuggestionsModal.tsx';
import { useFetchSuggestionCount } from './hooks/useFetchSuggestionCount.ts';
import { StackedContentIcons } from './StackedContentIcons.tsx';
import { EXTERNAL_PRODUCT_CONNECTION } from './__types__/RemoteObject.ts';
import { ThirdPartyAuthentication } from './ThirdPartyAuthentication.tsx';
import { CloseMenu } from './CloseMenu.tsx';
import { useDismissCuration } from './hooks/useDismissCuration.ts';
import {
	PageTreeForceFetchSubscriber,
	type ForceFetchPageIds,
} from './PageTreeForceFetchSubscriber.tsx';

const styles = cssMap({
	container: {
		backgroundColor: token('color.background.accent.blue.subtlest'),
		borderRadius: token('radius.small'),
		marginTop: token('space.100'),
		overflow: 'hidden',
		padding: token('space.200'),
		position: 'relative',
	},
	topRow: {
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		marginBottom: token('space.050'),
	},
	iconsWrapper: {
		display: 'flex',
		alignItems: 'center',
		flexShrink: 0,
	},
	mainAction: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: token('space.050'),
		backgroundColor: 'transparent',
		padding: token('space.0'),
		width: '100%',
		textAlign: 'start',
	},
});

const i18n = defineMessages({
	addContent: {
		id: 'space-curation.space-navigation.related-content.add-content',
		defaultMessage: 'Add suggested content from your connected work apps',
		description: 'Title for the suggestion entry point in the space navigation',
	},
	beta: {
		id: 'space-curation.space-navigation.related-content.beta',
		defaultMessage: 'Beta',
		description: 'Beta lozenge label displayed next to the suggestion entry point',
	},
	betaUpdated: {
		id: 'space-curation.space-navigation.related-content.beta.updated',
		defaultMessage: 'Beta',
		description: 'Sentence case Beta lozenge label displayed next to the suggestion entry point',
	},
	addContentAction: {
		id: 'space-curation.space-navigation.related-content.add-content-action',
		defaultMessage: 'Review and add',
		description: 'Label for the button inside the suggestion entry point card',
	},
});

type SpaceNavigationRelatedContentProps = {
	spaceId?: string;
};

type SpaceNavigationRelatedContentBodyProps = {
	spaceKey: string;
	mountTimestamp: number;
	suggestionCount: number;
	onDismiss: () => void;
	onNeverShowAgain: () => void;
	onModalClose: (hadRejections: boolean) => void | Promise<void>;
	onSaveSuccessRefetch: () => void;
	spaceId?: string;
};

const SpaceNavigationRelatedContentBody = ({
	spaceKey,
	mountTimestamp,
	suggestionCount,
	onDismiss,
	onNeverShowAgain,
	onModalClose,
	onSaveSuccessRefetch,
	spaceId,
}: SpaceNavigationRelatedContentBodyProps) => {
	const intl = useIntl();
	const [modalOpenTimestamp, setModalOpenTimestamp] = useState<number | null>(null);
	const isModalOpen = modalOpenTimestamp !== null;
	const forceFetchPageIdsRef = useRef<ForceFetchPageIds | null>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const mountValuesRef = useRef({
		spaceId,
		spaceKey,
		suggestionCount,
		mountTimestamp,
		createAnalyticsEvent,
	});

	useEffect(() => {
		const {
			spaceId: si,
			spaceKey: sk,
			suggestionCount: sc,
			mountTimestamp: mt,
			createAnalyticsEvent: cae,
		} = mountValuesRef.current;
		cae({
			type: 'sendUIEvent',
			data: {
				action: 'rendered',
				actionSubject: 'curationEntry',
				source: 'containerNavigation',
				attributes: {
					spaceId: si,
					spaceKey: sk,
					entryPoint: 'suggestionsEntry',
					suggestionCount: sc,
					renderLatencyMs: Date.now() - mt,
				},
			},
		}).fire();
	}, []);

	const handleDismiss = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'dismissed',
				actionSubject: 'curationEntry',
				source: 'containerNavigation',
				attributes: {
					spaceId,
					spaceKey,
					entryPoint: 'suggestionsEntry',
					dismissMethod: 'close_button',
					suggestionCountAtDismiss: suggestionCount,
				},
			},
		}).fire();
		onDismiss();
	};

	const handleNeverShowAgain = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'dismissed',
				actionSubject: 'curationEntry',
				source: 'containerNavigation',
				attributes: {
					spaceId,
					spaceKey,
					entryPoint: 'suggestionsEntry',
					dismissMethod: 'never_show_again',
					suggestionCountAtDismiss: suggestionCount,
				},
			},
		}).fire();
		onNeverShowAgain();
	};

	const handleOpenModal = () => {
		setModalOpenTimestamp(Date.now());
	};
	const handleForceFetchPageIdsChange = useCallback(
		(forceFetchPageIds: ForceFetchPageIds | null) => {
			forceFetchPageIdsRef.current = forceFetchPageIds;
		},
		[],
	);

	return (
		<>
			<Box xcss={styles.container} testId="space-navigation-related-content">
				<Box xcss={styles.topRow}>
					<Box xcss={styles.iconsWrapper}>
						<StackedContentIcons variant="medium" icons={['loom', 'google-drive', 'onedrive']} />
					</Box>
					<CloseMenu onDismiss={handleDismiss} onNeverShowAgain={handleNeverShowAgain} />
				</Box>
				<Pressable xcss={styles.mainAction} onClick={handleOpenModal}>
					<Text>
						{intl.formatMessage(i18n.addContent)}{' '}
						<Lozenge appearance="accent-gray">
							{intl.formatMessage(
								fg('confluence_lozenge_text_update') ? i18n.betaUpdated : i18n.beta,
							)}
						</Lozenge>
					</Text>
					<Text color="color.link" weight="medium">
						{intl.formatMessage(i18n.addContentAction)}
					</Text>
				</Pressable>
			</Box>
			<ModalTransition>
				{isModalOpen && modalOpenTimestamp !== null && (
					<ReviewContentSuggestionsModal
						spaceKey={spaceKey}
						spaceId={spaceId}
						modalOpenTimestamp={modalOpenTimestamp}
						onClose={async (hadRejections: boolean) => {
							setModalOpenTimestamp(null);
							await onModalClose(hadRejections);
						}}
						onSaveSuccess={(acceptedParentIds: number[]) => {
							setModalOpenTimestamp(null);
							onSaveSuccessRefetch();
							forceFetchPageIdsRef.current?.(acceptedParentIds);
						}}
					/>
				)}
			</ModalTransition>
			<PageTreeForceFetchSubscriber onForceFetchPageIdsChange={handleForceFetchPageIdsChange} />
		</>
	);
};

export const SpaceNavigationRelatedContent = ({ spaceId }: SpaceNavigationRelatedContentProps) => {
	const [isVisible, setIsVisible] = useState(true);
	const [spaceKey] = usePageSpaceKey();
	const mountTimestampRef = useRef(Date.now());
	const { dismissAction } = useDismissCuration();
	const { cloudId } = useSessionData();
	const {
		count,
		isLoading: isLoadingCount,
		isSuppressed,
		refetch: refetchSuggestionCount,
	} = useFetchSuggestionCount({ spaceKey, cloudId });
	const {
		unauthenticatedServices,
		isLoading: isLoadingThirdPartyAuth,
		refetch: refetchThirdPartyAuth,
		markOptimisticallyAuthenticated,
	} = useFetch3PAuthentications({
		products: [
			EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE,
			EXTERNAL_PRODUCT_CONNECTION.MICROSOFT_TEAMS,
		],
		cloudId,
	});

	if (!spaceKey || isPersonalSpace(spaceKey) || !isVisible || isLoadingCount || isSuppressed) {
		return null;
	}

	if (
		!isLoadingCount &&
		!isLoadingThirdPartyAuth &&
		count === 0 &&
		Object.keys(unauthenticatedServices).length != 0
	) {
		manuallyLogExpExposure('confluence_autonomous_space_curation');
		return (
			<ThirdPartyAuthentication
				unauthenticatedServices={unauthenticatedServices}
				onThirdPartyAuthRefetch={refetchThirdPartyAuth}
				onAuthSuccess={markOptimisticallyAuthenticated}
				spaceId={spaceId}
				mountTimestamp={mountTimestampRef.current}
			/>
		);
	}

	if (count > 0) {
		manuallyLogExpExposure('confluence_autonomous_space_curation');
		return (
			<SpaceNavigationRelatedContentBody
				spaceKey={spaceKey}
				mountTimestamp={mountTimestampRef.current}
				suggestionCount={count}
				onDismiss={async () => {
					try {
						await dismissAction({ remindTomorrow: true });
						setIsVisible(false);
					} catch {
						// If the dismiss POST fails, keep the card visible so the
						// preference is not silently lost.
					}
				}}
				onNeverShowAgain={async () => {
					try {
						await dismissAction({ neverShowAgain: true });
						setIsVisible(false);
					} catch {
						// If the never-show-again POST fails, keep the card visible so
						// the preference is not silently lost.
					}
				}}
				onSaveSuccessRefetch={refetchSuggestionCount}
				onModalClose={(hadRejections: boolean) => {
					if (hadRejections) {
						refetchSuggestionCount();
					}
				}}
				spaceId={spaceId}
			/>
		);
	}
};
